import React, { useCallback, useEffect, useRef, useState } from "react";
import { applyAuthUserUpdate } from "../../../config/runtime";
import { cancelSubscriptionRenewal, fetchAccount, fetchSubscriptionOverview, resumeSubscriptionRenewal } from "../../account/api";
import Button from "../../../ui/Button";
import GlassCard from "../../../ui/GlassCard";
import { C } from "../../../core/constants";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useDisplayTimezone } from "../../timezone/TimezoneProvider";

/** PayPal subscription details + cancel renewal — shown on Credits & Plans when user has active subscription. */
function resolveRenewalManagedVia(membership) {
  const v = membership?.renewal_managed_via;
  if (v) return v;
  const bp = membership?.billing_provider || membership?.provider;
  if (bp === "paypal") return "paypal";
  if (bp === "momo") return "momo";
  return "unknown";
}

function MembershipStatTile({ label, value, acc, valueColor }) {
  const vColor = valueColor || C.textPrimary;
  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: 14,
        border: `1px solid ${acc.primary}22`,
        background: "rgba(255,255,255,0.03)",
        minHeight: 76,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: C.textMuted,
          letterSpacing: "0.12em",
          marginBottom: 8,
          textTransform: "uppercase",
          lineHeight: 1.25,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          color: vColor,
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function MembershipSection({ acc, authContext, t, locale }) {
  const fs = APP_FONT_SIZE;
  const accountUser = authContext.user || {};
  const activeSubscription = Boolean(accountUser.activeSubscription ?? accountUser.active_subscription);

  const [membership, setMembership] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState("");
  const [membershipNotice, setMembershipNotice] = useState("");
  const [renewalActionBusy, setRenewalActionBusy] = useState(false);
  const [cancelRenewalModalOpen, setCancelRenewalModalOpen] = useState(false);

  const { timezone, formatInTimezone } = useDisplayTimezone();
  const tRef = useRef(t);
  tRef.current = t;

  const formatPeriodDate = useCallback(
    (iso) => {
      if (!iso) return "—";
      return formatInTimezone(iso, {
        locale,
        timeZone: timezone,
        dateStyle: "medium",
      });
    },
    [formatInTimezone, locale, timezone],
  );

  useEffect(() => {
    if (!activeSubscription) return;
    let cancelled = false;
    (async () => {
      setMembershipLoading(true);
      setMembershipError("");
      try {
        const data = await fetchSubscriptionOverview();
        if (!cancelled) setMembership(data.subscription || null);
      } catch (err) {
        if (!cancelled) setMembershipError(err.message || tRef.current("membership_load_failed"));
      } finally {
        if (!cancelled) setMembershipLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSubscription]);

  const confirmTurnOffRenewal = useCallback(async () => {
    if (renewalActionBusy || !membership?.can_cancel_in_app || membership?.renewal_cancelled_at) return;
    setCancelRenewalModalOpen(false);
    setRenewalActionBusy(true);
    setMembershipNotice("");
    setMembershipError("");
    try {
      const data = await cancelSubscriptionRenewal();
      if (data.subscription) setMembership(data.subscription);
      const accResp = await fetchAccount();
      if (authContext.user && accResp.user) applyAuthUserUpdate(authContext.user, accResp.user);
      const until = formatPeriodDate(
        data.subscription?.access_ends_at || data.subscription?.access_until,
      );
      setMembershipNotice(t("membership_cancel_done").replace("%{date}", until));
    } catch (err) {
      setMembershipError(err.message || t("save_failed"));
    } finally {
      setRenewalActionBusy(false);
    }
  }, [authContext, renewalActionBusy, formatPeriodDate, membership, t]);

  useEffect(() => {
    if (!cancelRenewalModalOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !renewalActionBusy) setCancelRenewalModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelRenewalModalOpen, renewalActionBusy]);

  const onResumeSubscriptionRenewal = useCallback(async () => {
    if (renewalActionBusy || !membership?.can_cancel_in_app || !membership?.renewal_cancelled_at) return;
    setRenewalActionBusy(true);
    setMembershipNotice("");
    setMembershipError("");
    try {
      const data = await resumeSubscriptionRenewal();
      if (data.subscription) setMembership(data.subscription);
      const accResp = await fetchAccount();
      if (authContext.user && accResp.user) applyAuthUserUpdate(authContext.user, accResp.user);
      setMembershipNotice(t("membership_resume_done"));
    } catch (err) {
      setMembershipError(err.message || t("save_failed"));
    } finally {
      setRenewalActionBusy(false);
    }
  }, [authContext, renewalActionBusy, membership, t]);

  if (!activeSubscription) return null;

  const managedVia =
    membership && membership.active ? resolveRenewalManagedVia(membership) : null;

  return (
    <div style={{ marginTop: 20, marginBottom: 4 }}>
      <GlassCard
        accent={acc.secondary}
        style={{
          padding: 20,
          overflow: "hidden",
          border: `1px solid ${acc.secondary}40`,
          background: `linear-gradient(145deg, rgba(255,255,255,0.055), ${acc.secondary}10)`,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 14,
            marginBottom: membershipLoading ? 0 : 14,
          }}
        >
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ fontSize: 12, letterSpacing: "0.12em", color: C.textMuted, marginBottom: 8 }}>
              {t("membership_heading").toUpperCase()}
            </div>
            {membershipLoading ? (
              <div style={{ fontSize: 14, color: C.textSecondary }}>{t("membership_loading")}</div>
            ) : membership && membership.active ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <h2
                    style={{
                      fontFamily: "'Cormorant Garamond',serif",
                      fontSize: 30,
                      fontWeight: 400,
                      color: C.textPrimary,
                      margin: 0,
                      lineHeight: 1.15,
                    }}
                  >
                    {membership.plan_display_name}
                  </h2>
                  <span
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: acc.secondary,
                      border: `1px solid ${acc.secondary}66`,
                      background: `${acc.secondary}18`,
                      padding: "6px 10px",
                      borderRadius: 999,
                    }}
                  >
                    {t("membership_active_badge")}
                  </span>
                </div>
              </>
            ) : (
              !membershipError && (
                <div style={{ fontSize: 14, color: C.textMuted }}>{t("membership_load_failed")}</div>
              )
            )}
          </div>
          {!membershipLoading && membership && membership.active && membership.can_cancel_in_app ? (
            !membership.renewal_cancelled_at ? (
              <button
                type="button"
                disabled={renewalActionBusy}
                onClick={() => {
                  if (!renewalActionBusy) setCancelRenewalModalOpen(true);
                }}
                style={{
                  padding: "8px 14px",
                  fontSize: 12,
                  minHeight: 38,
                  lineHeight: 1.3,
                  borderRadius: 10,
                  border: `1px solid ${acc.primary}55`,
                  background: `${acc.primary}10`,
                  color: renewalActionBusy ? C.textMuted : C.textSecondary,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 500,
                  letterSpacing: "0.02em",
                  cursor: renewalActionBusy ? "not-allowed" : "pointer",
                  opacity: renewalActionBusy ? 0.55 : 1,
                  transition:
                    "opacity 0.2s ease, border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease",
                }}
              >
                {renewalActionBusy ? t("membership_cancel_busy") : t("membership_cancel_renewal_button")}
              </button>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <Button
                  variant="primary"
                  acc={acc}
                  disabled={renewalActionBusy}
                  onClick={onResumeSubscriptionRenewal}
                  style={{
                    padding: "10px 20px",
                    fontSize: 14,
                    minHeight: 46,
                    fontWeight: 600,
                    opacity: renewalActionBusy ? 0.7 : 1,
                  }}
                >
                  {renewalActionBusy ? t("membership_cancel_busy") : t("membership_turn_on_renewal_button")}
                </Button>
                {membership.paypal_manage_autopay_url ? (
                  <a
                    href={membership.paypal_manage_autopay_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12, color: C.textMuted, textUnderlineOffset: 3 }}
                  >
                    {t("membership_resume_fallback_link")}
                  </a>
                ) : null}
              </div>
            )
          ) : null}
        </div>

        {membershipError ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(255,100,120,0.1)",
              border: "1px solid rgba(255,120,140,0.25)",
              fontSize: 13,
              color: "#ffb8c6",
              marginBottom: membership && membership.active ? 14 : 0,
            }}
          >
            {membershipError}
          </div>
        ) : null}

        {membershipNotice ? (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: `${acc.secondary}14`,
              border: `1px solid ${acc.secondary}44`,
              fontSize: 13,
              color: C.textSecondary,
              marginBottom: membership && membership.active ? 14 : 0,
            }}
          >
            {membershipNotice}
          </div>
        ) : null}

        {!membershipLoading && membership && membership.active ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                gap: 10,
              }}
            >
              {membership.price_display ? (
                <MembershipStatTile
                  label={t("membership_price_label")}
                  value={membership.price_display}
                  acc={acc}
                  valueColor={acc.secondary}
                />
              ) : null}
              <MembershipStatTile
                label={
                  membership.renewal_cancelled_at
                    ? t("membership_period_ends_label")
                    : t("membership_period_renews_label")
                }
                value={formatPeriodDate(
                  membership.access_ends_at || membership.access_until || membership.next_renewal_at,
                )}
                acc={acc}
                valueColor={C.textPrimary}
              />
            </div>
            {membership.paypal_status && managedVia === "paypal" ? (
              <div style={{ marginTop: 14, fontSize: 12, color: C.textMuted }}>
                {t("membership_paypal_status_label")}:{" "}
                <span style={{ color: C.textSecondary }}>{membership.paypal_status}</span>
              </div>
            ) : null}

            {(managedVia === "paypal" || membership.billing_provider === "paypal") ? (
              <div style={{ marginTop: 10, fontSize: fs - 1, color: C.textMuted, lineHeight: 1.5 }}>
                {membership.renewal_cancelled_at ? t("membership_turn_on_renewal_hint") : t("membership_renewal_note")}
              </div>
            ) : managedVia === "momo" ? (
              <div style={{ marginTop: 10, fontSize: fs - 1, color: C.textMuted, lineHeight: 1.5 }}>{t("membership_renewal_note_momo")}</div>
            ) : null}

            {(() => {
              let banner = "";
              let warnStyle = false;
              if (membership.can_cancel_in_app && membership.renewal_cancelled_at) {
                banner = t("membership_renewal_already_cancelled").replace(
                  "%{date}",
                  formatPeriodDate(membership.access_ends_at || membership.access_until),
                );
              } else if (!membership.can_cancel_in_app && managedVia === "paypal") {
                banner = t("membership_legacy_notice");
                warnStyle = true;
              } else if (!membership.can_cancel_in_app && managedVia === "momo") {
                banner = t("membership_manage_momo_notice");
                warnStyle = true;
              } else if (!membership.can_cancel_in_app && managedVia === "unknown") {
                banner = t("membership_manage_unknown_notice");
                warnStyle = true;
              }
              if (!banner) return null;
              return (
                <div
                  style={{
                    marginTop: 16,
                    padding: "14px 16px",
                    borderRadius: 14,
                    border: `1px solid ${warnStyle ? `${acc.secondary}44` : acc.primary}`,
                    background: warnStyle ? "rgba(255,224,163,0.06)" : `${acc.primary}0d`,
                    fontSize: fs - 1,
                    color: warnStyle ? "#ffe0a3" : "#fff",
                    lineHeight: 1.5,
                  }}
                >
                  {banner}
                </div>
              );
            })()}
          </>
        ) : null}
      </GlassCard>

      {cancelRenewalModalOpen ? (
        <div
          role="presentation"
          onClick={() => {
            if (!renewalActionBusy) setCancelRenewalModalOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            background: "rgba(5,8,24,0.78)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="membership-cancel-renewal-title" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 420 }}>
            <GlassCard
              accent={acc.secondary}
              style={{
                padding: "28px 26px",
                border: `1px solid ${acc.secondary}55`,
                background: "linear-gradient(165deg, rgba(12,18,40,0.98), rgba(16,22,48,0.94))",
                boxShadow: `0 24px 48px rgba(0,0,0,0.45), 0 0 0 1px ${acc.secondary}22 inset`,
              }}
            >
              <div style={{ fontSize: 11, letterSpacing: "0.14em", color: acc.secondary, marginBottom: 10, textTransform: "uppercase" }}>
                {t("membership_cancel_modal_eyebrow")}
              </div>
              <h2
                id="membership-cancel-renewal-title"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 28,
                  fontWeight: 400,
                  color: C.textPrimary,
                  margin: "0 0 14px",
                  lineHeight: 1.2,
                }}
              >
                {t("membership_cancel_modal_title")}
              </h2>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: fs - 1, color: C.textSecondary, lineHeight: 1.55, margin: "0 0 26px" }}>
                {t("membership_cancel_modal_body")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "flex-end" }}>
                <Button
                  variant="outline"
                  acc={acc}
                  disabled={renewalActionBusy}
                  onClick={() => setCancelRenewalModalOpen(false)}
                  style={{
                    padding: "10px 20px",
                    fontSize: 13,
                    minHeight: 44,
                    border: `1px solid ${acc.primary}55`,
                    background: `${acc.primary}0f`,
                  }}
                >
                  {t("membership_cancel_modal_keep")}
                </Button>
                <button
                  type="button"
                  disabled={renewalActionBusy}
                  onClick={() => {
                    confirmTurnOffRenewal();
                  }}
                  style={{
                    padding: "10px 20px",
                    fontSize: 13,
                    minHeight: 44,
                    borderRadius: 12,
                    border: "1px solid rgba(255,120,140,0.45)",
                    background: "rgba(255,100,120,0.12)",
                    color: "#ffc9d2",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    cursor: renewalActionBusy ? "not-allowed" : "pointer",
                    opacity: renewalActionBusy ? 0.65 : 1,
                    transition: "background 0.2s ease, border-color 0.2s ease",
                  }}
                >
                  {renewalActionBusy ? t("membership_cancel_busy") : t("membership_cancel_modal_confirm")}
                </button>
              </div>
            </GlassCard>
          </div>
        </div>
      ) : null}
    </div>
  );
}
